/**
 * Keyboard Event Debugging Utilities
 *
 * This module provides utilities for debugging keyboard events and shortcuts
 * in the application. Use these tools to understand keyboard behavior and
 * troubleshoot issues.
 */

export interface KeyboardEventInfo {
  key: string;
  code: string;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
  target: EventTarget | null;
  activeElement: Element | null;
  timestamp: string;
  eventType: 'keydown' | 'keyup' | 'keypress';
}

export interface ShortcutInfo {
  key: string;
  description: string;
  modifiers: string[];
  registered: boolean;
}

class KeyboardDebugger {
  private events: KeyboardEventInfo[] = [];
  private maxEvents = 100; // Keep last 100 events
  private isEnabled = false;

  /**
   * Enable keyboard event debugging
   */
  enable() {
    this.isEnabled = true;
    console.log('🔧 Keyboard debugging enabled');
  }

  /**
   * Disable keyboard event debugging
   */
  disable() {
    this.isEnabled = false;
    console.log('🔧 Keyboard debugging disabled');
  }

  /**
   * Log a keyboard event
   */
  logEvent(
    event: KeyboardEvent | React.KeyboardEvent,
    eventType: 'keydown' | 'keyup' | 'keypress' = 'keydown'
  ) {
    if (!this.isEnabled) return;

    // Skip events with undefined key to prevent errors
    if (!event.key) {
      console.warn('⚠️ Skipping keyboard event with undefined key:', event);
      return;
    }

    const eventInfo: KeyboardEventInfo = {
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      target: event.target,
      activeElement: document.activeElement,
      timestamp: new Date().toISOString(),
      eventType,
    };

    this.events.unshift(eventInfo);
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }

    console.log(`🔑 ${eventType.toUpperCase()}:`, eventInfo);
  }

  /**
   * Get recent keyboard events
   */
  getRecentEvents(count: number = 10): KeyboardEventInfo[] {
    return this.events.slice(0, count);
  }

  /**
   * Get events for a specific key
   */
  getEventsForKey(key: string): KeyboardEventInfo[] {
    return this.events.filter(event => event.key && event.key.toLowerCase() === key.toLowerCase());
  }

  /**
   * Clear all logged events
   */
  clearEvents() {
    this.events = [];
    console.log('🧹 Keyboard event log cleared');
  }

  /**
   * Print a summary of recent events
   */
  printSummary() {
    console.log('📊 Keyboard Event Summary:');
    console.log(`Total events logged: ${this.events.length}`);

    const keyCounts = this.events.reduce(
      (acc, event) => {
        acc[event.key] = (acc[event.key] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    console.log('Key frequency:', keyCounts);

    const recentEvents = this.getRecentEvents(5);
    console.log('Recent events:', recentEvents);
  }

  /**
   * Check if a key combination is being detected
   */
  testKeyCombination(
    key: string,
    modifiers: { ctrl?: boolean; alt?: boolean; shift?: boolean; meta?: boolean } = {}
  ) {
    const matchingEvents = this.events.filter(event => {
      return (
        event.key &&
        event.key.toLowerCase() === key.toLowerCase() &&
        event.ctrlKey === !!modifiers.ctrl &&
        event.altKey === !!modifiers.alt &&
        event.shiftKey === !!modifiers.shift &&
        event.metaKey === !!modifiers.meta
      );
    });

    console.log(`🔍 Testing key combination: ${key} with modifiers`, modifiers);
    console.log(`Found ${matchingEvents.length} matching events:`, matchingEvents);

    return matchingEvents;
  }
}

// Create a singleton instance
export const keyboardDebugger = new KeyboardDebugger();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).keyboardDebugger = keyboardDebugger;
}

/**
 * Hook for easy keyboard debugging in React components
 */
export const useKeyboardDebugger = () => {
  return {
    enable: () => keyboardDebugger.enable(),
    disable: () => keyboardDebugger.disable(),
    logEvent: (
      event: KeyboardEvent | React.KeyboardEvent,
      eventType?: 'keydown' | 'keyup' | 'keypress'
    ) => keyboardDebugger.logEvent(event, eventType),
    getRecentEvents: (count?: number) => keyboardDebugger.getRecentEvents(count),
    getEventsForKey: (key: string) => keyboardDebugger.getEventsForKey(key),
    clearEvents: () => keyboardDebugger.clearEvents(),
    printSummary: () => keyboardDebugger.printSummary(),
    testKeyCombination: (
      key: string,
      modifiers?: { ctrl?: boolean; alt?: boolean; shift?: boolean; meta?: boolean }
    ) => keyboardDebugger.testKeyCombination(key, modifiers),
  };
};
